import {
  filterContactInput,
  contactErrors,
  validateEin,
} from "../../../../utils/contact-validation.ts";
import { useState, type FormEvent } from "react";
import { RadioGroup, TextField } from "../../../ui/index.ts";
import { WizardRailLayout } from "../../onboarding-shared/wizard-rail-layout.tsx";
import { BUSINESS_FIELDS, BUSINESS_TYPES, OTHER_BUSINESS_FIELD, OTHER_PAYMENT_TERMS_FIELD, CONSULTANT_FIELDS, INITIAL_VALUES, PAYMENT_FIELDS, PAYMENT_METHODS, PAYMENT_TERMS, STEPS, type FieldDefinition, type FieldKey } from "./consultant-us.config.ts";
import { ConsultantUsDocuments, type ConsultantDocuments } from "./consultant-us-documents.tsx";
import { ConsultantUsReview } from "./consultant-us-review.tsx";
import { useOnboardingSubmission } from "../shared/use-onboarding-submission.ts";
import { mapUsConsultant } from "../shared/submission-mappers.ts";
import { showError } from "../../../../utils/toast.ts";
import { validateAgencyFile } from "../consultant-agency-us/consultant-agency-us.config.ts";
import styles from "./consultant-us.module.css";

export function ConsultantUsForm({ onSubmitted }: { onSubmitted?: () => void }) {
  const submission = useOnboardingSubmission("consultant");
  const requiredFields: FieldKey[][] = [
    ["firstName", "lastName", "phoneNumber"],
    [
      "businessName",
      "businessAddress",
      "city",
      "state",
      "zip",
      "country",
      "businessType",
      "taxId",
      "stateOfFormation",
    ],
    ["accountName", "accountNumber", "bankName", "routingNumber"],
  ];
  const [step, setStep] = useState(0);
  const [values, setValues] = useState(() => ({ ...INITIAL_VALUES, firstName: submission.verified?.firstName ?? "", lastName: submission.verified?.lastName ?? "", personalEmail: submission.verified?.email ?? "" }));
  const [files, setFiles] = useState<ConsultantDocuments>({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<FieldKey | "acknowledgment", string>>>({});

  const update = (key: FieldKey, value: string) => {
    if (key === "zip") value = value.replace(/\D/g, "").slice(0, 5);
    else if (key === "taxId") value = value.replace(/[^\d-]/g, "").slice(0, 10);
    else value = filterContactInput(key, value);
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
    setAcknowledged(false);
    setReviewed(false);
  };

  const fields = (definitions: FieldDefinition[]) => definitions.map(({ key, label, placeholder, type, hint }) => (
    <TextField
      key={key}
      id={`consultant-us-${key}`}
      label={label}
      required={requiredFields.flat().includes(key)}
      readOnly={key === "personalEmail" && Boolean(submission.verified)}
      type={type ?? "text"}
      hint={hint}
      autoComplete="off"
      maxLength={
        key === "businessAddress"
          ? 500
          : key === "phoneNumber"
            ? 10
            : 150
      } value={values[key]}
      error={errors[key]}
      placeholder={placeholder}
      onChange={(event) => update(key, event.target.value)}
    />
  ));

  const next = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submission.busy || submission.complete) return;
    const nextErrors: typeof errors = step === 4 ? contactErrors(values) : step === 0 ? contactErrors({ firstName: values.firstName, lastName: values.lastName, preferredName: values.preferredName, phoneNumber: values.phoneNumber }) : step === 2 ? contactErrors({ bankName: values.bankName }) : {};
    for (const key of (step === 4 ? requiredFields.flat() : requiredFields[step] ?? [])) {
      if (!values[key].trim()) nextErrors[key] = "This field is required.";
    }
    if (step === 1) {
      if (!/^\d{5}$/.test(values.zip.trim())) {
        nextErrors.zip = "Enter a valid 5-digit ZIP/Postal Code.";
      }

      const einError = validateEin(values.taxId);
      if (einError) nextErrors.taxId = einError;
    }

    if (step >= 3) {
      if (!files.tax) { showError("Upload your completed W-9 or applicable W-8."); setStep(3); return; }
      for (const file of Object.values(files)) {
        if (file) { const error = validateAgencyFile(file); if (error) { showError(error); setStep(3); return; } }
      }
    }
    const emailKey = step === 0 ? "personalEmail" : step === 2 ? "remittanceEmail" : null;
    if (emailKey && values[emailKey].trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values[emailKey].trim())) {
      nextErrors[emailKey] = "Enter a valid email address.";
    }
    if (step === 1 && values.businessType === "other" && !values.otherBusinessType.trim()) nextErrors.otherBusinessType = "Specify your business type.";
    if (step === 2 && values.paymentTerms === "other" && !values.otherPaymentTerms.trim()) nextErrors.otherPaymentTerms = "Specify your payment terms.";
    if (step === 4 && !acknowledged) nextErrors.acknowledgment = "Please acknowledge the information above to finish your review.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      if (step === 4) {
        const invalidStep = requiredFields.findIndex((keys) => keys.some((key) => nextErrors[key]));
        if (Object.keys(contactErrors(values)).some((key) => key !== "bankName")) setStep(0);
        else if (invalidStep >= 0) setStep(invalidStep);
      }
      return;
    }
    if (step < 4) setStep((current) => current + 1);
    else if (await submission.submit(mapUsConsultant(values), files)) { setReviewed(true); onSubmitted?.(); }
  };

  return (
    <WizardRailLayout
      steps={STEPS}
      currentStepIndex={step}
      sectionTitle={STEPS[step].label}
      onBack={submission.busy || submission.complete ? undefined : () => { setErrors({}); setReviewed(false); setStep((current) => Math.max(0, current - 1)); }}
      primaryLabel={submission.complete ? "Submitted" : step === 4 ? "Submit" : "Continue"}
      primaryDisabled={reviewed || submission.busy || submission.complete}
      primaryLoading={submission.busy}
      primaryFormId="consultant-us-form"
    >
      <form id="consultant-us-form" onSubmit={next} noValidate>
        <fieldset disabled={submission.busy || submission.complete} className={styles.formFields}>
          {step === 0 && <div className={styles.grid}>{fields(CONSULTANT_FIELDS)}</div>}
          {step === 1 && (
            <div className={styles.stack}>
              <div className={styles.grid}>{fields(BUSINESS_FIELDS)}</div>
              <RadioGroup name="consultant-us-business-type" label="Business Type" options={BUSINESS_TYPES} value={values.businessType} onChange={(value) => update("businessType", value)} />
              {values.businessType === "other" && fields([OTHER_BUSINESS_FIELD])}
            </div>
          )}
          {step === 2 && (
            <div className={styles.stack}>
              <RadioGroup name="consultant-us-payment-method" label="Payment Method" options={PAYMENT_METHODS} value={values.paymentMethod} onChange={(value) => update("paymentMethod", value)} />
              <div className={styles.grid}>{fields(PAYMENT_FIELDS)}</div>
              <RadioGroup name="consultant-us-payment-terms" label="Payment Terms" options={PAYMENT_TERMS} value={values.paymentTerms} onChange={(value) => update("paymentTerms", value)} />
              {values.paymentTerms === "other" && fields([OTHER_PAYMENT_TERMS_FIELD])}
            </div>
          )}
          {step === 3 && <ConsultantUsDocuments files={files} onChange={(id, file) => {
            setFiles((current) => {
              const nextFiles = { ...current };
              if (file) nextFiles[id] = file;
              else delete nextFiles[id];
              return nextFiles;
            });
            setAcknowledged(false);
            setReviewed(false);
          }} />}
          {step === 4 && <>
            <ConsultantUsReview values={values} files={files} acknowledged={acknowledged} error={errors.acknowledgment} onAcknowledge={(checked) => { setAcknowledged(checked); setReviewed(false); setErrors({}); }} />
          </>}
        </fieldset>
        {submission.complete && <p role="status">Your onboarding form was submitted successfully.</p>}
      </form>
    </WizardRailLayout>
  );
}
